import { DateTime } from "luxon";
import { ShowingStatus, ShowingType } from "api/scheduling/types";

export const showingTypeLabels: Record<ShowingType, string> = {
  [ShowingType.Guided]: "Guided",
  [ShowingType.SelfGuided]: "Self-guided"
};

export const colors = {
  content100: "#bec0ca",
  content200: "#A6A8B1",
  content500: "#53545C",

  alpha40: "rgba(28,29,34,0.040)",
  alpha80: "rgba(28,29,34,0.080)",
  alpha100: "rgba(28,29,34,0.1)",

  danger500: "#F4423F"
};

interface ShowingStatusConfig {
  label: string;
  dotClass: string;
  textClass: string;
  bgClass: string;
  eventBg: string;
}

export const showingStatusConfig: Record<ShowingStatus, ShowingStatusConfig> = {
  [ShowingStatus.Confirmed]: {
    label: "Scheduled",
    dotClass: "bg-primary-500",
    textClass: "text-primary-700",
    bgClass: "bg-primary-20",
    eventBg: "var(--primary-20)"
  },
  [ShowingStatus.LeadConfirmed]: {
    label: "Confirmed",
    dotClass: "bg-teal-500",
    textClass: "text-teal-700",
    bgClass: "bg-teal-50",
    eventBg: "#f0fdfa"
  },
  [ShowingStatus.Ongoing]: {
    label: "Ongoing",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
    bgClass: "bg-amber-50",
    eventBg: "#fffbeb"
  },
  [ShowingStatus.InProgress]: {
    label: "In progress",
    dotClass: "bg-warning-500",
    textClass: "text-warning-700",
    bgClass: "bg-warning-50",
    eventBg: "#fef6e6"
  },
  [ShowingStatus.Completed]: {
    label: "Completed",
    dotClass: "bg-success-500",
    textClass: "text-success-700",
    bgClass: "bg-success-50",
    eventBg: "#effbeb"
  },
  [ShowingStatus.NoShow]: {
    label: "No-show",
    dotClass: "bg-rose-500",
    textClass: "text-rose-700",
    bgClass: "bg-rose-50",
    eventBg: "#fff1f2"
  },
  [ShowingStatus.Cancelled]: {
    label: "Canceled",
    dotClass: "bg-gray-400",
    textClass: "text-gray-600",
    bgClass: "bg-gray-100",
    eventBg: "#f3f4f6"
  },
  [ShowingStatus.Declined]: {
    label: "Declined",
    dotClass: "bg-gray-400",
    textClass: "text-gray-600",
    bgClass: "bg-gray-100",
    eventBg: "#f3f4f6"
  },
  [ShowingStatus.Pending]: {
    label: "Pending confirmation",
    dotClass: "bg-warning-500",
    textClass: "text-warning-700",
    bgClass: "bg-warning-50",
    eventBg: "#fef6e6"
  },
  [ShowingStatus.Reserved]: {
    label: "Reserved",
    dotClass: "bg-primary-500",
    textClass: "text-primary-500",
    bgClass: "bg-primary-50",
    eventBg: "var(--primary-50)"
  },
  [ShowingStatus.Expired]: {
    label: "Confirmation expired",
    dotClass: "bg-gray-400",
    textClass: "text-gray-600",
    bgClass: "bg-alpha-40",
    eventBg: "#f3f4f6"
  }
};

export const DECLINABLE_STATUSES: ReadonlySet<ShowingStatus> = new Set([
  ShowingStatus.Pending
]);

export const CANCELLABLE_STATUSES: ReadonlySet<ShowingStatus> = new Set([
  ShowingStatus.Confirmed,
  ShowingStatus.LeadConfirmed,
  ShowingStatus.Ongoing
]);

const RESCHEDULE_REQUESTED_LABEL = "Reschedule requested";

export const showingStatusLabel = (
  status: ShowingStatus,
  reschedulePending?: boolean
): string =>
  status === ShowingStatus.Pending && reschedulePending
    ? RESCHEDULE_REQUESTED_LABEL
    : showingStatusConfig[status].label;

export const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return {
    value,
    label: `${hour === 0 || hour === 12 ? 12 : hour % 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`
  };
});

export const TIME_OPTIONS_5MIN = Array.from({ length: 288 }, (_, i) => {
  const hour = Math.floor(i / 12);
  const minute = (i % 12) * 5;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return {
    value,
    label: `${hour === 0 || hour === 12 ? 12 : hour % 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`
  };
});

export const roundToNearest5 = (n: number): number => Math.round(n / 5) * 5;

export const formatDurationLabel = (minutes: number): string => {
  if (minutes === 60) return "1 hr";
  if (minutes === 90) return "1.5 hr";
  if (minutes === 120) return "2 hr";
  return `${minutes} min`;
};

export const formatDurationLong = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (mins) parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
};

const STANDARD_DURATIONS = [15, 20, 30, 45, 60, 90, 120] as const;

type DurationButton =
  | { kind: "standard"; minutes: number; isDefault: boolean }
  | { kind: "custom" };

export const buildDurationButtons = (
  defaultMinutes: number
): DurationButton[] => {
  const standards = [...STANDARD_DURATIONS];
  const isInStandards = standards.includes(
    defaultMinutes as (typeof STANDARD_DURATIONS)[number]
  );

  const minutesList: number[] = isInStandards
    ? standards
    : [...standards, defaultMinutes].sort((a, b) => a - b);

  const buttons: DurationButton[] = minutesList.map((m) => ({
    kind: "standard",
    minutes: m,
    isDefault: m === defaultMinutes
  }));

  buttons.push({ kind: "custom" });
  return buttons;
};

const roundAndFormat = (h: number, m: number): string | null => {
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  // Cap at 23:55 so late times like 11:58 PM don't wrap to 00:00.
  const rounded = Math.min(23 * 60 + 55, roundToNearest5(h * 60 + m));
  const rh = Math.floor(rounded / 60);
  const rm = rounded % 60;
  return `${String(rh).padStart(2, "0")}:${String(rm).padStart(2, "0")}`;
};

export const parseTypedTime = (input: string): string | null => {
  const cleaned = input.trim().toUpperCase();
  if (!cleaned) return null;

  const ampmMatch = cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = ampmMatch[2] ? parseInt(ampmMatch[2], 10) : 0;
    if (ampmMatch[3] === "AM" && h === 12) h = 0;
    if (ampmMatch[3] === "PM" && h !== 12) h += 12;
    return roundAndFormat(h, m);
  }

  const noSepMatch = cleaned.match(/^(\d{3,4})$/);
  if (noSepMatch) {
    const s = noSepMatch[1].padStart(4, "0");
    return roundAndFormat(
      parseInt(s.slice(0, 2), 10),
      parseInt(s.slice(2), 10)
    );
  }

  const colonMatch = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (colonMatch)
    return roundAndFormat(
      parseInt(colonMatch[1], 10),
      parseInt(colonMatch[2], 10)
    );

  return null;
};

const formatTimeLabel = (time: string): string => {
  const [h, m] = time.split(":").map(Number);
  const display = h === 0 || h === 12 ? 12 : h % 12;
  return `${display}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export const buildToTimeOptions = (
  fromTime: string,
  durationMinutes: number
): { value: string; label: string }[] => {
  if (durationMinutes <= 0) return [];
  const [fh, fm] = fromTime.split(":").map(Number);
  const startMin = fh * 60 + fm;
  const opts: { value: string; label: string }[] = [];
  for (let t = startMin + durationMinutes; t < 24 * 60; t += durationMinutes) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    opts.push({ value, label: formatTimeLabel(value) });
  }
  return opts;
};

export const addMinutesToTime = (time: string, minutes: number): string => {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
};

export const clampStartTime = (
  time: string,
  durationMinutes: number,
  gridMinutes = 15
): string => {
  const step = durationMinutes > 0 ? durationMinutes : gridMinutes;
  const [h, m] = time.split(":").map(Number);
  const maxStart =
    Math.floor((24 * 60 - step - gridMinutes) / gridMinutes) * gridMinutes;
  if (h * 60 + m <= maxStart) return time;
  return `${String(Math.floor(maxStart / 60)).padStart(2, "0")}:${String(maxStart % 60).padStart(2, "0")}`;
};

export const getDefaultStartTime = (
  timezone: string,
  durationMinutes: number,
  gridMinutes = 15
): string => {
  const step = durationMinutes > 0 ? durationMinutes : gridMinutes;
  const now = DateTime.now().setZone(timezone);
  const minutesNow = now.hour * 60 + now.minute;

  let start = Math.ceil(minutesNow / step) * step;
  start = Math.ceil(start / gridMinutes) * gridMinutes;

  const maxStart = 24 * 60 - step - gridMinutes;
  if (start > maxStart) start = maxStart;
  if (start < 0) start = 0;

  const h = Math.floor(start / 60);
  const m = start % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
